import { useEffect, useMemo, useReducer, useRef } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Dayjs } from 'dayjs';

import { CallerCount, CrmCallFilter } from '../callLog/callLog';
import { CRM_FOLLOW_UPS } from './followUps.queries';
import { buildFollowUpsFilter, FollowUpRow, FollowUpsState } from './followUps';

interface FollowUpsData {
  crmCalls: {
    hasNextPage: boolean;
    endCursor: string | null;
    byCaller?: Array<CallerCount>;
    items: Array<FollowUpRow>;
  };
  due?: { total: number };
  upcoming?: { total: number };
}

export interface FollowUpsCounts {
  due: number;
  upcoming: number;
  byCaller: Array<CallerCount>;
}

interface PageRequest {
  filter: CrmCallFilter;
  take: number;
  after?: string;
  skip?: number;
  withCounts: boolean;
  dueFilter?: CrmCallFilter;
  upcomingFilter?: CrmCallFilter;
}

const codeOf = (error: ApolloError | undefined) => error?.graphQLErrors?.[0]?.extensions?.code;
const fieldOf = (error: ApolloError | undefined) => error?.graphQLErrors?.[0]?.extensions?.field;

const countsOf = (data: FollowUpsData | undefined): FollowUpsCounts | undefined =>
  data?.due && data.upcoming && data.crmCalls.byCaller
    ? { due: data.due.total, upcoming: data.upcoming.total, byCaller: data.crmCalls.byCaller }
    : undefined;

/**
 * One page of follow-ups, asked as the call log asks (useCallLogPage):
 *
 * - **Next steps by cursor** (`after`); a page reached any other way (a link,
 *   a reload, the way back from a contact) by `skip`.
 * - **The counts once per filter:** each view's total and who set them.
 *   Paging does not change them.
 * - **A page's request is fixed the first time it is asked,** so coming back
 *   to it is answered from Apollo's cache.
 */
export const useFollowUpsPage = (state: FollowUpsState, now: Dayjs) => {
  const filter = useMemo(() => buildFollowUpsFilter(state.view, state.by, now), [state.view, state.by, now]);
  const filterKey = JSON.stringify(filter);
  const listKey = JSON.stringify([filterKey, state.pageSize]);
  const requestKey = `${listKey}#${state.page}`;

  const memory = useRef({
    cursors: new Map<string, Map<number, string>>(),
    requests: new Map<string, PageRequest>(),
    counts: new Map<string, FollowUpsCounts>(),
  });
  const [, forget] = useReducer((n: number) => n + 1, 0);

  const cursorsFor = (key: string) => {
    let cursors = memory.current.cursors.get(key);
    if (!cursors) {
      cursors = new Map();
      memory.current.cursors.set(key, cursors);
    }
    return cursors;
  };

  let request = memory.current.requests.get(requestKey);
  if (!request) {
    const after = state.page > 0 ? cursorsFor(listKey).get(state.page) : undefined;
    const withCounts = !memory.current.counts.has(filterKey);
    request = {
      filter,
      take: state.pageSize,
      ...(after ? { after } : state.page > 0 ? { skip: state.page * state.pageSize } : {}),
      withCounts,
      ...(withCounts
        ? {
            dueFilter: buildFollowUpsFilter('due', state.by, now),
            upcomingFilter: buildFollowUpsFilter('upcoming', state.by, now),
          }
        : {}),
    };
    memory.current.requests.set(requestKey, request);
  }

  const { data, previousData, loading, error } = useQuery<FollowUpsData>(CRM_FOLLOW_UPS, {
    variables: request,
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.crmCalls;

  useEffect(() => {
    if (!data) return;
    const counts = countsOf(data);
    if (counts) memory.current.counts.set(filterKey, counts);
    if (page?.hasNextPage && page.endCursor) cursorsFor(listKey).set(state.page + 1, page.endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // A cursor the API no longer accepts: drop it and ask by number instead.
  const staleCursor = Boolean(request.after) && codeOf(error) === 'CRM_INVALID_QUERY' && fieldOf(error) === 'after';
  useEffect(() => {
    if (!staleCursor) return;
    memory.current.requests.delete(requestKey);
    memory.current.cursors.delete(listKey);
    forget();
  }, [staleCursor, requestKey, listKey]);

  /** After a call or a close: everything remembered is out of date. The cache is cleared by the caller. */
  const reset = () => {
    memory.current.requests.clear();
    memory.current.cursors.clear();
    memory.current.counts.clear();
    forget();
  };

  const shown = page ?? previousData?.crmCalls;

  return {
    rows: shown?.items ?? [],
    loading,
    error: staleCursor ? undefined : error,
    hasNextPage: Boolean(page?.hasNextPage),
    counts: countsOf(data) ?? memory.current.counts.get(filterKey),
    reset,
  };
};
